import { AxiosError } from 'axios';
import { apiClient } from './api';
import {
  ICalFeedConfiguration,
  ConfigureICalFeedRequest,
  ICalFeedEvent,
  ICalFeedSourceKey,
  ICalFeedStatus,
  ReleaseState,
} from '../types/icalFeed';

/** Discriminant for owner-facing errors surfaced by release/reblock actions. */
export type IcalActionErrorCode =
  | 'NOT_FOUND'
  | 'CONFIRMED_BOOKING_NOT_RELEASABLE'
  | 'ALREADY_RELEASED'
  | 'NOT_RELEASED'
  | 'PLATFORM_BOOKING_EXISTS'
  | 'FEED_NOT_ACTIVE'
  | 'UNKNOWN';

export interface IcalActionError {
  readonly code: IcalActionErrorCode;
  readonly cause: unknown;
}

/**
 * Maps a release/reblock Axios error into a stable, owner-facing error code.
 * Backend contract: 404 => not found; 409 with a `code` body field distinguishes
 * the specific conflict reason.
 */
export const mapIcalActionError = (err: unknown): IcalActionError => {
  const status = (err as AxiosError)?.response?.status;
  const bodyCode = (err as AxiosError<{ code?: string }>)?.response?.data?.code;

  if (status === 404) {
    return { code: 'NOT_FOUND', cause: err };
  }

  if (status === 409) {
    if (bodyCode === 'CONFIRMED_BOOKING_NOT_RELEASABLE') {
      return { code: 'CONFIRMED_BOOKING_NOT_RELEASABLE', cause: err };
    }
    if (bodyCode === 'ALREADY_RELEASED') {
      return { code: 'ALREADY_RELEASED', cause: err };
    }
    if (bodyCode === 'NOT_RELEASED') {
      return { code: 'NOT_RELEASED', cause: err };
    }
    if (bodyCode === 'PLATFORM_BOOKING_EXISTS') {
      return { code: 'PLATFORM_BOOKING_EXISTS', cause: err };
    }
    if (bodyCode === 'FEED_NOT_ACTIVE') {
      return { code: 'FEED_NOT_ACTIVE', cause: err };
    }
  }

  return { code: 'UNKNOWN', cause: err };
};

interface ApiICalFeedResponse {
  readonly id?: string;
  readonly apartmentId?: string;
  readonly sourceLabel?: string;
  readonly feedUrl?: string;
  readonly status?: string;
  readonly lastSyncedAt?: string;
  readonly lastSyncError?: string;
}

interface ApiICalFeedEventResponse {
  readonly id?: string;
  readonly apartmentId?: string;
  readonly feedId?: string;
  readonly feedSourceLabel?: string;
  readonly icalUid?: string;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly summary?: string;
  readonly eventType?: string;
  readonly bookingId?: string;
  readonly availabilityId?: string;
  readonly releaseState?: string;
  readonly releasedAt?: string;
  readonly conflictReason?: string;
  readonly conflictDetectedAt?: string;
  readonly lastSeenAt?: string;
  readonly disappearedAt?: string;
  readonly createdAt?: string;
}

interface AddICalFeedRequest {
  readonly sourceLabel: string;
  readonly feedUrl: string;
}

interface ListICalFeedEventsOptions {
  readonly includeDisappeared?: boolean;
  readonly icalFeedConfigurationId?: string;
  readonly sourceKey?: ICalFeedSourceKey;
  readonly eventType?: 'BOOKING' | 'BLOCKED';
  readonly releaseState?: ReleaseState;
  readonly conflict?: boolean;
}

interface UpsertICalFeedRequest extends ConfigureICalFeedRequest {
  readonly sourceKey?: ICalFeedSourceKey;
  readonly sourceLabel?: string;
  readonly icalFeedConfigurationId?: string;
}

const SOURCE_LABEL_BY_KEY: Record<
  'AIRBNB' | 'BOOKING' | 'EXPEDIA' | 'FEWO_DIREKT' | 'VRBO' | 'TRIPADVISOR',
  string
> = {
  AIRBNB: 'Airbnb',
  BOOKING: 'Booking.com',
  EXPEDIA: 'Expedia',
  FEWO_DIREKT: 'FeWo-direkt',
  VRBO: 'Vrbo',
  TRIPADVISOR: 'TripAdvisor',
};

const deriveSourceKey = (sourceLabel?: string): ICalFeedSourceKey | undefined => {
  if (!sourceLabel) return undefined;
  const normalized = sourceLabel.trim().toUpperCase();

  if (normalized === 'AIRBNB') return 'AIRBNB';
  if (normalized === 'BOOKING' || normalized === 'BOOKING.COM') return 'BOOKING';
  if (normalized === 'EXPEDIA') return 'EXPEDIA';
  if (normalized === 'FEWO-DIREKT' || normalized === 'FEWO_DIREKT' || normalized === 'FEWODIREKT') {
    return 'FEWO_DIREKT';
  }
  if (normalized === 'VRBO') return 'VRBO';
  if (normalized === 'TRIPADVISOR' || normalized === 'TRIP_ADVISOR') return 'TRIPADVISOR';

  return undefined;
};

const toAddFeedRequest = (request: UpsertICalFeedRequest): AddICalFeedRequest => {
  const sourceLabel = request.sourceLabel?.trim();
  if (sourceLabel) {
    return {
      sourceLabel,
      feedUrl: request.feedUrl,
    };
  }

  const mappedSourceLabel =
    request.sourceKey && request.sourceKey in SOURCE_LABEL_BY_KEY
      ? SOURCE_LABEL_BY_KEY[request.sourceKey as keyof typeof SOURCE_LABEL_BY_KEY]
      : request.sourceKey;

  return {
    sourceLabel: mappedSourceLabel ?? 'Airbnb',
    feedUrl: request.feedUrl,
  };
};

const mapFeed = (feed: ApiICalFeedResponse): ICalFeedConfiguration => ({
  apartmentId: feed.apartmentId,
  sourceKey: deriveSourceKey(feed.sourceLabel),
  sourceLabel: feed.sourceLabel,
  icalFeedConfigurationId: feed.id,
  feedUrl: feed.feedUrl,
  status: feed.status as ICalFeedStatus | undefined,
  lastSyncedAt: feed.lastSyncedAt,
  lastSyncError: feed.lastSyncError,
});

const mapEvent = (event: ApiICalFeedEventResponse): ICalFeedEvent => ({
  id: event.id,
  apartmentId: event.apartmentId,
  icalFeedConfigurationId: event.feedId,
  sourceKey: deriveSourceKey(event.feedSourceLabel),
  sourceLabel: event.feedSourceLabel,
  icalUid: event.icalUid,
  startDate: event.startDate,
  endDate: event.endDate,
  summary: event.summary,
  eventType: event.eventType,
  bookingId: event.bookingId,
  availabilityId: event.availabilityId,
  releaseState: event.releaseState,
  releasedAt: event.releasedAt,
  conflictReason: event.conflictReason,
  conflictDetectedAt: event.conflictDetectedAt,
  lastSeenAt: event.lastSeenAt,
  disappearedAt: event.disappearedAt,
  createdAt: event.createdAt,
});

export const icalFeedService = {
  async listFeeds(apartmentId: string): Promise<ICalFeedConfiguration[]> {
    try {
      const response = await apiClient.get<ApiICalFeedResponse[]>(
        `/apartments/${apartmentId}/ical-feeds`
      );
      return response.data.map(mapFeed);
    } catch (err) {
      if ((err as AxiosError)?.response?.status === 404) return [];
      throw err;
    }
  },

  async createFeed(
    apartmentId: string,
    request: UpsertICalFeedRequest
  ): Promise<ICalFeedConfiguration> {
    const response = await apiClient.post<ApiICalFeedResponse>(
      `/apartments/${apartmentId}/ical-feeds`,
      toAddFeedRequest(request)
    );
    return mapFeed(response.data);
  },

  async updateFeed(
    apartmentId: string,
    icalFeedConfigurationId: string,
    request: UpsertICalFeedRequest
  ): Promise<ICalFeedConfiguration> {
    const response = await apiClient.put<ApiICalFeedResponse>(
      `/apartments/${apartmentId}/ical-feeds/${icalFeedConfigurationId}`,
      toAddFeedRequest(request)
    );
    return mapFeed(response.data);
  },

  async deleteFeed(apartmentId: string, icalFeedConfigurationId?: string): Promise<void> {
    if (!icalFeedConfigurationId) {
      const feeds = await this.listFeeds(apartmentId);
      const firstFeedId = feeds[0]?.icalFeedConfigurationId;
      if (!firstFeedId) {
        return;
      }
      await apiClient.delete(`/apartments/${apartmentId}/ical-feeds/${firstFeedId}`);
      return;
    }

    await apiClient.delete(`/apartments/${apartmentId}/ical-feeds/${icalFeedConfigurationId}`);
  },

  async listFeedEvents(
    apartmentId: string,
    options: ListICalFeedEventsOptions = {}
  ): Promise<ICalFeedEvent[]> {
    const includeDisappeared = options.includeDisappeared ?? false;
    const events = await this.listEvents(apartmentId, includeDisappeared, {
      feedId: options.icalFeedConfigurationId,
      eventType: options.eventType,
      releaseState: options.releaseState,
      conflict: options.conflict,
    });

    if (!options.sourceKey) {
      return events;
    }

    return events.filter(event => {
      const matchesSource = !options.sourceKey || event.sourceKey === options.sourceKey;
      return matchesSource;
    });
  },

  /**
   * GET /apartments/{id}/ical-feeds
   * Returns the first configured iCal feed for the apartment, or `null` when no feed
   * is configured (HTTP 404). All other errors are re-thrown.
   */
  async getFeed(apartmentId: string): Promise<ICalFeedConfiguration | null> {
    const feeds = await this.listFeeds(apartmentId);
    return feeds[0] ?? null;
  },

  /**
   * Backward-compatible helper that configures a default Airbnb feed.
   */
  async configureFeed(
    apartmentId: string,
    request: ConfigureICalFeedRequest
  ): Promise<ICalFeedConfiguration> {
    return this.createFeed(apartmentId, {
      feedUrl: request.feedUrl,
      sourceKey: 'AIRBNB',
    });
  },

  /**
   * Backward-compatible helper that removes the first available configured feed.
   */
  async removeFeed(apartmentId: string): Promise<void> {
    await this.deleteFeed(apartmentId);
  },

  /**
   * GET /apartments/{id}/ical-feeds/events
   * Lists imported iCal events for the apartment.
   * Pass feedId to scope results to a single feed.
   * Pass `includeDisappeared=true` to include events no longer in the remote feed.
   */
  async listEvents(
    apartmentId: string,
    includeDisappeared = false,
    feedIdOrOptions?:
      | string
      | {
          readonly feedId?: string;
          readonly eventType?: 'BOOKING' | 'BLOCKED';
          readonly releaseState?: ReleaseState;
          readonly conflict?: boolean;
        }
  ): Promise<ICalFeedEvent[]> {
    const extra =
      typeof feedIdOrOptions === 'string' ? { feedId: feedIdOrOptions } : feedIdOrOptions ?? {};

    const response = await apiClient.get<ApiICalFeedEventResponse[]>(
      `/apartments/${apartmentId}/ical-feeds/events`,
      {
        params: {
          feedId: extra.feedId,
          includeDisappeared,
          eventType: extra.eventType,
          releaseState: extra.releaseState,
          conflict: extra.conflict,
        },
      }
    );
    return response.data.map(mapEvent);
  },

  /**
   * POST /apartments/{apartmentId}/ical-feeds/events/{eventId}/release
   * Releases an imported external block, making its dates available on this platform.
   * Only valid for eventType=BLOCKED events with releaseState !== 'RELEASED'.
   */
  async release(apartmentId: string, eventId: string): Promise<ICalFeedEvent> {
    try {
      const response = await apiClient.post<ApiICalFeedEventResponse>(
        `/apartments/${apartmentId}/ical-feeds/events/${eventId}/release`
      );
      return mapEvent(response.data);
    } catch (err) {
      throw mapIcalActionError(err);
    }
  },

  /**
   * POST /apartments/{apartmentId}/ical-feeds/events/{eventId}/reblock
   * Re-blocks a previously released imported external block.
   * Rejected with PLATFORM_BOOKING_EXISTS when an overlapping platform booking exists.
   */
  async reblock(apartmentId: string, eventId: string): Promise<ICalFeedEvent> {
    try {
      const response = await apiClient.post<ApiICalFeedEventResponse>(
        `/apartments/${apartmentId}/ical-feeds/events/${eventId}/reblock`
      );
      return mapEvent(response.data);
    } catch (err) {
      throw mapIcalActionError(err);
    }
  },

  /**
   * POST /apartments/{apartmentId}/ical-feeds/{icalFeedConfigurationId}/sync
   * (backend: `ApartmentICalController.syncICalFeedNow`)
   * Triggers an immediate reconcile/import cycle for a single feed instead of waiting
   * for the hourly background sync. Only accepted while the feed's `status` is `ACTIVE`
   * — callers should gate the "Sync now" action client-side on that, and still handle a
   * 409 (`FEED_NOT_ACTIVE`) from a stale UI state gracefully.
   */
  async syncFeedNow(
    apartmentId: string,
    icalFeedConfigurationId: string
  ): Promise<ICalFeedConfiguration> {
    try {
      const response = await apiClient.post<ApiICalFeedResponse>(
        `/apartments/${apartmentId}/ical-feeds/${icalFeedConfigurationId}/sync`
      );
      return mapFeed(response.data);
    } catch (err) {
      throw mapIcalActionError(err);
    }
  },
};
